/**
 * Audience emotes on the presenter deck: one floating emoji per reaction (tap);
 * a small handful when long-press boosted. Default: bottom-left stage; last slide: full-width bottom, larger.
 * Optional label for a11y. Mounts under `.reveal` when present so emotes stay on the slide area (not over the chat drawer);
 * otherwise `document.fullscreenElement` when fullscreen; else `document.body`.
 */

import { REMOTE_EMOTE_FINALE_SIZE_MULT, remoteEmoteParticleSpawn } from './emote-layout';
import type { RemoteEmoteLayout } from './emote-layout';

export type { RemoteEmoteLayout } from './emote-layout';

const ROOT_CLASS = 'lectura-remote-emote-toast-root';
const BURST_CLASS = 'lectura-remote-emote-burst';

function getEmoteOverlayParent(): HTMLElement {
  const doc = document as Document & { webkitFullscreenElement?: Element | null };
  const fs = document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
  if (fs && fs instanceof HTMLElement) return fs;
  const reveal = document.querySelector('.reveal');
  if (reveal instanceof HTMLElement) return reveal;
  return document.body;
}

function ensureEmoteRoot(): HTMLDivElement {
  const parent = getEmoteOverlayParent();
  let root = document.querySelector<HTMLDivElement>(`.${ROOT_CLASS}`);
  if (!root) {
    root = document.createElement('div');
    root.className = ROOT_CLASS;
    root.setAttribute('aria-live', 'polite');
  }
  if (root.parentElement !== parent) {
    parent.appendChild(root);
  }
  return root;
}

let fullscreenListenerBound = false;
function bindFullscreenReparentOnce(): void {
  if (fullscreenListenerBound || typeof document === 'undefined') return;
  fullscreenListenerBound = true;
  const onFs = (): void => {
    const root = document.querySelector<HTMLDivElement>(`.${ROOT_CLASS}`);
    if (!root) return;
    const parent = getEmoteOverlayParent();
    if (root.parentElement !== parent) {
      parent.appendChild(root);
    }
  };
  document.addEventListener('fullscreenchange', onFs);
  document.addEventListener('webkitfullscreenchange', onFs as EventListener);
}

function clampEmoteScale(raw: number | undefined): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return 1;
  return Math.min(2.25, Math.max(1, raw));
}

export function showRemoteEmoteToast(
  emoji: string,
  opts?: {
    label?: string;
    durationMs?: number;
    scale?: number;
    /** `last_slide`: full bottom band + larger particles (Reveal final slide). */
    layout?: RemoteEmoteLayout;
  }
): void {
  const trimmed = String(emoji || '').trim();
  if (!trimmed) return;

  const layout: RemoteEmoteLayout = opts?.layout === 'last_slide' ? 'last_slide' : 'default';

  bindFullscreenReparentOnce();
  const root = ensureEmoteRoot();

  const scale = clampEmoteScale(opts?.scale);
  const reduced =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const burst = document.createElement('div');
  burst.className = BURST_CLASS;
  if (layout === 'last_slide') {
    burst.classList.add('lectura-remote-emote-burst--finale');
  }
  burst.setAttribute('role', 'status');
  burst.style.setProperty('--lectura-emote-scale', String(scale));
  burst.style.setProperty(
    '--lectura-emote-finale-mult',
    layout === 'last_slide' ? String(REMOTE_EMOTE_FINALE_SIZE_MULT) : '1'
  );

  const label = opts?.label?.trim();
  if (label) {
    const sr = document.createElement('span');
    sr.className = 'lectura-remote-emote-sr-only';
    sr.textContent = `${label}: ${trimmed}`;
    burst.appendChild(sr);
  }

  const stage = document.createElement('div');
  stage.className =
    layout === 'last_slide'
      ? 'lectura-remote-emote-stage lectura-remote-emote-stage--finale'
      : 'lectura-remote-emote-stage';

  if (reduced) {
    const simple = document.createElement('div');
    simple.className =
      layout === 'last_slide'
        ? 'lectura-remote-emote-simple lectura-remote-emote-simple--finale'
        : 'lectura-remote-emote-simple';
    simple.textContent = trimmed;
    if (label) simple.setAttribute('aria-label', `${label}: ${trimmed}`);
    stage.appendChild(simple);
  } else {
    const layer = document.createElement('div');
    layer.className = 'lectura-remote-emote-particles';
    const boosted = scale > 1.35;
    const spawn = remoteEmoteParticleSpawn(layout, boosted);
    for (let i = 0; i < spawn.count; i += 1) {
      const p = document.createElement('span');
      p.className = 'lectura-remote-emote-particle';
      p.setAttribute('aria-hidden', 'true');
      p.textContent = trimmed;
      p.style.left = `${spawn.leftPct()}%`;
      p.style.bottom = `${spawn.bottomPct()}%`;
      p.style.setProperty('--lectura-emote-rise', `${spawn.riseMin + Math.random() * spawn.riseSpan}vmin`);
      p.style.setProperty('--lectura-emote-dx', `${spawn.dxMin + Math.random() * spawn.dxSpan}vmin`);
      p.style.setProperty('--lectura-emote-rot', `${(Math.random() - 0.5) * spawn.rotScale}deg`);
      p.style.animationDelay = `${Math.random() * spawn.delayMax}s`;
      p.style.animationDuration = `${spawn.durMin + Math.random() * spawn.durSpan}s`;
      const jitter = spawn.jitterMin + Math.random() * spawn.jitterSpan;
      p.style.setProperty('--lectura-emote-size-jitter', String(jitter));
      layer.appendChild(p);
    }
    stage.appendChild(layer);
  }

  burst.appendChild(stage);

  root.appendChild(burst);
  void burst.getBoundingClientRect();
  requestAnimationFrame(() => {
    burst.classList.add('lectura-remote-emote-burst--visible');
  });

  const rawMs = opts?.durationMs;
  const durationMs =
    typeof rawMs === 'number' && Number.isFinite(rawMs)
      ? Math.min(Math.max(Math.round(rawMs), 1600), 22000)
      : 4200;

  const remove = (): void => {
    burst.classList.remove('lectura-remote-emote-burst--visible');
    burst.classList.add('lectura-remote-emote-burst--out');
    const done = (): void => {
      burst.removeEventListener('animationend', done);
      burst.remove();
      if (root && !root.querySelector(`.${BURST_CLASS}`)) {
        root.remove();
      }
    };
    burst.addEventListener('animationend', done, { once: true });
    window.setTimeout(done, 900);
  };

  window.setTimeout(remove, durationMs);
}
